import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import {
  HERO_PRESETS,
  HERO_PRESET_ORDER,
  HERO_PRESET_STORAGE_KEY,
  DEFAULT_HERO_PRESET,
} from "../homepageHero";
import {
  fetchHomepageHeroPreset,
  setHomepageHeroPreset,
} from "../../lib/api";

/**
 * Admin control for the seasonal homepage hero swap.
 *
 * - Two-option picker with human labels ("Summer / General",
 *   "Report Card Season"). Never a raw text field.
 * - Displays which preset is currently live.
 * - PUT is admin-gated on the backend (require_admin_user).
 * - On success we also refresh the localStorage cache the homepage
 *   reader uses, so an admin opening the homepage in the same session
 *   immediately sees the new preset with no flash.
 */
const HomepageHeroPanel = () => {
  const [active, setActive] = useState(null); // server-known live preset
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetchHomepageHeroPreset()
      .then((data) => {
        if (cancelled) return;
        const key =
          data && Object.prototype.hasOwnProperty.call(HERO_PRESETS, data.preset)
            ? data.preset
            : DEFAULT_HERO_PRESET;
        setActive(key);
      })
      .catch(() => {
        if (!cancelled) setActive(DEFAULT_HERO_PRESET);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const activate = async (presetKey) => {
    if (saving || presetKey === active) return;
    setSaving(true);
    try {
      const res = await setHomepageHeroPreset(presetKey);
      const nextKey = res?.preset || presetKey;
      setActive(nextKey);
      try {
        window.localStorage.setItem(HERO_PRESET_STORAGE_KEY, nextKey);
      } catch (_e) {
        /* ignore */
      }
      toast.success(`Homepage hero set to "${HERO_PRESETS[nextKey].label}"`, {
        description: "Public homepage will render this preset immediately.",
      });
    } catch (err) {
      toast.error("Could not update homepage hero", {
        description: err?.response?.data?.detail || err?.message || "Unknown error",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ae-card p-6 md:p-7" data-testid="admin-homepage-hero-card">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h3 className="font-extrabold text-lg text-[#4a6984] tracking-tight">
          Homepage hero
        </h3>
        {loading ? (
          <span className="inline-flex items-center gap-2 text-xs font-semibold text-[#4a6984]/60">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
          </span>
        ) : (
          <span
            data-testid="admin-homepage-hero-active-label"
            className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-[#1c9e7e]"
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
            Live: {HERO_PRESETS[active || DEFAULT_HERO_PRESET].label}
          </span>
        )}
      </div>

      <p className="text-sm text-[#4a6984]/85 leading-relaxed mb-5">
        Pick which seasonal message runs on the public homepage. Changes
        take effect immediately — no redeploy needed. Switch is manual;
        the homepage falls back to <strong>Summer / General</strong> if
        the setting can&apos;t be read.
      </p>

      <div
        className="grid grid-cols-1 md:grid-cols-2 gap-4"
        data-testid="admin-homepage-hero-options"
      >
        {HERO_PRESET_ORDER.map((key) => {
          const preset = HERO_PRESETS[key];
          const isActive = key === active;
          return (
            <button
              key={key}
              type="button"
              disabled={saving || isActive}
              onClick={() => activate(key)}
              data-testid={`admin-homepage-hero-option-${key}`}
              className={[
                "text-left rounded-2xl border-2 p-5 transition-all",
                isActive
                  ? "border-[#1c9e7e] bg-[#96ded1]/15 shadow-md cursor-default"
                  : "border-[#4a6984]/15 bg-white hover:border-[#4a6984]/40 hover:shadow-sm cursor-pointer",
                saving && !isActive ? "opacity-60 cursor-wait" : "",
              ].join(" ")}
            >
              <div className="flex items-center justify-between gap-2 mb-3">
                <p className="font-bold uppercase text-[10px] tracking-[0.2em] text-[#4a6984]/70">
                  {preset.label}
                </p>
                {isActive && (
                  <span
                    data-testid={`admin-homepage-hero-active-badge-${key}`}
                    className="inline-flex items-center gap-1 rounded-full bg-[#1c9e7e] text-white text-[10px] font-extrabold uppercase tracking-[0.14em] px-2 py-0.5"
                  >
                    <CheckCircle2 className="h-3 w-3" /> Active
                  </span>
                )}
              </div>
              <p className="font-extrabold text-[#1c2d3d] text-base leading-snug">
                {preset.headline}
              </p>
              <p className="mt-3 text-[13.5px] text-[#4a6984] leading-relaxed">
                {preset.subhead}
              </p>
              {!isActive && (
                <p className="mt-4 text-xs font-bold uppercase tracking-[0.14em] text-[#4a6984]/70">
                  Click to make live →
                </p>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default HomepageHeroPanel;
